import { coverInk } from '@/components/covers/cover-tint';
import { GeneratedCover } from '@/components/covers/GeneratedCover';
import { getCategoryHue } from '@/lib/instrument-categories';
import { cn } from '@/lib/utils';
import type { Test } from '@/api/tests';

/**
 * The band is 64px tall, so the wordmark has to give ground as acronyms get
 * longer. Most sit at four or five characters (PHQ-9, GAD-7); the tail runs to
 * eight (M-CHAT-R, SCARED-P), and the `test_id` fallback in `parseInstrumentName`
 * has no bound at all — hence `truncate` underneath these steps.
 */
function wordmarkSize(acronym: string): string {
    if (acronym.length <= 5) return 'text-2xl';
    if (acronym.length <= 9) return 'text-xl';
    return 'text-lg';
}

interface InstrumentCardBannerProps {
    test: Test;
    acronym: string;
}

/**
 * Cover art for an instrument card.
 *
 * Deliberately unlike the report-template covers in one respect: the hue comes
 * from the category rather than from the item's own hash. Instruments already
 * carry a meaningful colour — the badge and the accent bar both key off
 * category — and a second, random colour per card would have competed with it.
 * The geometry is still seeded per instrument, so two tests in the same category
 * share a palette without sharing a shape.
 */
export default function InstrumentCardBanner({ test, acronym }: InstrumentCardBannerProps) {
    const hue = String(getCategoryHue(test.category));
    const ink = coverInk(hue);

    return (
        <div className="h-16 shrink-0">
            <GeneratedCover seed={test.id} hue={hue}>
                {/* Category and acronym share the ink tint and stack as one block,
                    so the pair reads as a single label on the art rather than as
                    two things that happen to sit near each other. Centred rather
                    than anchored to the bottom edge: the stack is one line shorter
                    when a test has no category, and a bottom anchor left those
                    cards looking like they had slipped. */}
                <div className="absolute inset-x-4 top-1/2 -translate-y-1/2" style={{ color: ink }}>
                    {test.category ? (
                        /* `leading-4` rather than `leading-none`: `truncate` sets
                           `overflow: hidden`, and at a line-height of exactly 1em
                           the box clips anything above cap height — which is where
                           the tilde of DEPRESSÃO and the acute of ÁLCOOL live. */
                        <p className="truncate text-[0.7rem] font-medium uppercase leading-4 tracking-wide opacity-80">
                            {/* Uppercased and stripped of context visually; the
                                prefix is what makes it land as a category rather
                                than as a second title when read aloud. */}
                            <span className="sr-only">Categoria: </span>
                            {test.category}
                        </p>
                    ) : null}

                    <h3
                        className={cn(
                            'mt-0.5 truncate font-semibold leading-none tracking-tight',
                            wordmarkSize(acronym),
                        )}
                        title={test.name}
                    >
                        {acronym}
                    </h3>
                </div>
            </GeneratedCover>
        </div>
    );
}
